/** Account menu in the top bar: who you are, your role, theme toggle, sign out. Adapted from Prism's ProfileMenu. */

import { useEffect, useRef, useState } from 'react';
import { CircleUserRound, LogOut, Moon, Sun } from 'lucide-react';
import { Badge, Button, IconButton, Separator } from '@/lib/macondo';
import { useAuth } from '@/auth/AuthContext';
import { useTheme } from '@/lib/theme';

export function UserMenu() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <IconButton
        type="button"
        size="sm"
        variant="ghost"
        aria-label="Account"
        aria-haspopup="menu"
        aria-expanded={open}
        title={user?.email}
        onClick={() => setOpen((v) => !v)}
        className="aria-expanded:bg-surface-secondary h-800 w-800"
        icon={<CircleUserRound size={18} aria-hidden="true" />}
      />
      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          className="border-border-primary bg-surface-primary shadow-popup absolute right-0 top-[calc(100%+8px)] z-[45] flex w-[260px] flex-col rounded-card-sm border p-100"
        >
          <div className="px-200 py-150">
            <div className="text-caption text-typography-tertiary uppercase tracking-wide">Signed in as</div>
            <div title={user?.email} className="text-callout-semibold text-typography-primary truncate">
              {user?.email}
            </div>
            <Badge size="sm" variant={user?.role === 'admin' ? 'attention' : user?.role === 'operator' ? 'info' : 'default1'} className="mt-100">
              {user?.role}
            </Badge>
          </div>
          <Separator className="my-100" />
          <Button type="button" role="menuitem" variant="list-item" onClick={() => toggleTheme()}>
            {theme === 'dark' ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
            {theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          </Button>
          <Separator className="my-100" />
          <Button
            type="button"
            role="menuitem"
            variant="list-item"
            loading={signingOut}
            onClick={() => {
              setSigningOut(true);
              void logout().finally(() => setSigningOut(false));
            }}
          >
            <LogOut size={16} aria-hidden="true" />
            Sign out
          </Button>
        </div>
      )}
    </div>
  );
}
